import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Save, Zap } from 'lucide-react'

import { PosCheckoutPayment } from './pos-checkout-payment'
import { PosInvoicePopup } from './pos-invoice-popup'
import {
    posMaterialEffective,
    posOrderItemIndex,
    posOrderTotals,
} from './pos-order-payload'
import { PosLotSection } from './pos-lot-section'
import type { OrderTab } from './pos-types'
import type { PosCheckoutMode } from '@/hooks/use-pos-checkout'
import type { InvoiceBuyerFormValue } from '@/components/forms/invoice-buyer-fields'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'
import { usePosCheckout } from '@/hooks/use-pos-checkout'
import { usePosLotAllocation } from '@/hooks/use-pos-lot-allocation'
import { useAuth } from '@/hooks/use-auth'
import { useFeatureFlag } from '@/hooks/use-feature-flag'
import { useInvoiceSettings } from '@/hooks/use-invoice-query'
import {
    DEFAULT_CREATE_INVOICE_BUYER,
    InvoiceBuyerFields,
    applyBuyerPatch,
    buildInvoiceBuyerPayload,
} from '@/components/forms/invoice-buyer-fields'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ShippingCarrierSelect } from '@/components/forms/shipping-carrier-select'
import { formatVND } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'
import { posthog } from '@/lib/posthog'
import { DEFAULT_SHIPPING_CARRIER_ID, WALKIN_CUSTOMER } from '@/lib/constants'

interface Props {
    open: boolean
    onOpenChange: (open: boolean) => void
    tab: OrderTab
    onSuccess: () => void
    /** Called immediately after the order is created (before invoice popup). Use to show QR in parent. */
    onOrderCreated?: (orderId: string, method: 'cash' | 'bank_transfer') => void
}

export function PosCheckoutModal({
    open,
    onOpenChange,
    tab,
    onSuccess,
    onOrderCreated,
}: Props) {
    const workflow = useWorkflowTracking('pos_checkout', {
        attemptKey: tab.id,
        active: open,
    })
    const checkout = usePosCheckout()
    const { hasPermission } = useAuth()
    const canEditDiscount = hasPermission('sales_orders.edit_discount')
    const { data: invoiceSettings } = useInvoiceSettings()
    const vatPercent = invoiceSettings?.vat_percent ?? 0

    const [orderDiscountPercent, setOrderDiscountPercent] = useState(0)
    const [customerPhone, setCustomerPhone] = useState(
        tab.customer?.phone ?? '',
    )
    const [selectedCarrierId, setSelectedCarrierId] = useState(
        DEFAULT_SHIPPING_CARRIER_ID,
    )
    // HĐĐT: nhóm "Chờ xuất hóa đơn" và tự phát hành Bán ngay chỉ tính đơn có tick issue_einvoice.
    const einvoiceEnabled = useFeatureFlag('dev-einvoice')
    const [invoiceBuyer, setInvoiceBuyer] = useState<InvoiceBuyerFormValue>(
        DEFAULT_CREATE_INVOICE_BUYER,
    )
    const [internalCode, setInternalCode] = useState('')
    const customerCredit = Math.max(0, -(tab.customer?.current_debt ?? 0))
    const [applyCredit, setApplyCredit] = useState(false)
    const lots = usePosLotAllocation(tab, open)

    // Dùng chung posOrderTotals với panel quick để mọi màn POS ra cùng một số
    // tiền (M-78: tổng theo giá gồm VAT từng đơn vị).
    const { priceOf: materialPriceOf } = posMaterialEffective(
        tab.items,
        tab.packagingItems,
    )
    const {
        itemSubtotal,
        materialTotal,
        baseAmount,
        subtotal,
        vatAmount,
        totalAmount,
    } = posOrderTotals(
        tab.items,
        tab.packagingItems,
        orderDiscountPercent,
        vatPercent,
    )
    const creditApplied = applyCredit
        ? Math.min(customerCredit, totalAmount)
        : 0
    const amountDue = Math.round(totalAmount - creditApplied)

    const [paymentMethod, setPaymentMethod] = useState<
        'cash' | 'bank_transfer'
    >('cash')
    const [customerPayment, setCustomerPayment] = useState(totalAmount)
    const [overpayAction, setOverpayAction] = useState<'change' | 'credit'>(
        'change',
    )
    const [invoiceOrder, setInvoiceOrder] = useState<{
        id: string
        code: string
        method: 'cash' | 'bank_transfer'
        issueEInvoice: boolean
    } | null>(null)

    // Reset state when modal opens
    useEffect(() => {
        if (open) {
            setOrderDiscountPercent(0)
            setCustomerPayment(
                tab.items.reduce((s, i) => s + i.quantity * i.unit_price, 0),
            )
            setOverpayAction('change')
            setPaymentMethod('cash')
            setInvoiceOrder(null)
            setCustomerPhone(tab.customer?.phone ?? '')
            setSelectedCarrierId(DEFAULT_SHIPPING_CARRIER_ID)
            setInvoiceBuyer(DEFAULT_CREATE_INVOICE_BUYER)
            setInternalCode('')
            setApplyCredit(false)
        }
    }, [open, tab.customer?.phone])

    // Số phải trả đổi theo credit cấn → đồng bộ ô tiền khách đưa.
    useEffect(() => {
        if (open) setCustomerPayment(amountDue)
    }, [open, amountDue])

    const hasCustomer = tab.customer !== null
    // Walk-in fallback: no selection => anonymous walk-in customer.
    // Phone field is optional in this case (uses walk-in seed phone).
    const phoneOk = hasCustomer ? customerPhone.trim().length >= 8 : true
    const hasSellable = tab.items.length > 0 || materialTotal > 0
    // Lỗi phân bổ lô KHÔNG khoá nút (AGENTS.md → Form Submit & Validation);
    // handleSubmit chặn và nói rõ lý do.
    const canSubmit = hasSellable && phoneOk && !checkout.isPending

    const handleSubmit = async (mode: PosCheckoutMode) => {
        if (!canSubmit) {
            workflow.step('validation_failed')
            return
        }
        // Lô chưa phân bổ đủ: chặn nhưng phải NÓI RÕ, không im lặng.
        if (lots.hasError) {
            workflow.step('validation_failed')
            toast.error(m.stock_out_submit_blocked())
            return
        }
        const buyer = tab.customer ?? WALKIN_CUSTOMER
        const phoneToSend = customerPhone.trim() || WALKIN_CUSTOMER.phone || ''
        try {
            const order = await workflow.run(
                () =>
                    checkout.mutateAsync({
                        order: {
                            source_channel: 'direct',
                            customer_id: buyer.id,
                            customer_name: buyer.name,
                            customer_phone: phoneToSend,
                            shipping_address:
                                buyer.address || m.customer_in_store(),
                            shipping_carrier_id:
                                selectedCarrierId ||
                                DEFAULT_SHIPPING_CARRIER_ID,
                            product_notes: tab.note || undefined,
                            issue_einvoice: einvoiceEnabled
                                ? invoiceBuyer.enabled
                                : false,
                            invoice_buyer: einvoiceEnabled
                                ? buildInvoiceBuyerPayload(
                                      invoiceBuyer,
                                      'create',
                                  )
                                : undefined,
                            internal_order_code:
                                internalCode.trim() || undefined,
                            apply_credit: applyCredit && customerCredit > 0,
                            vat_percent: vatPercent,
                            vat_amount: vatAmount,
                            discount_percent:
                                orderDiscountPercent > 0
                                    ? orderDiscountPercent
                                    : undefined,
                            items: tab.items.map((item) => ({
                                product_id: item.product_id,
                                quantity: item.quantity,
                                unit_price: item.unit_price,
                                discount_percent: item.discount_percent,
                                material_price_split:
                                    item.material_price_split || undefined,
                            })),
                            packaging_items: (tab.packagingItems ?? [])
                                .filter((p) => p.quantity > 0)
                                .map((p) => ({
                                    packaging_material_id:
                                        p.packaging_material_id,
                                    quantity: p.quantity,
                                    source: p.source,
                                    // Dòng GỘP gửi giá 0 (giá vật tư trong giá SP).
                                    unit_price: materialPriceOf(p),
                                    discount_percent: p.discount_percent,
                                    product_id: p.product_id || undefined,
                                    order_item_index: posOrderItemIndex(
                                        tab.items,
                                        p.line_key,
                                    ),
                                })),
                            ...(mode === 'pay_now' ? lots.buildPayload() : {}),
                        },
                        mode,
                        paymentMethod,
                        paymentAmount:
                            paymentMethod === 'cash' ? customerPayment : 0,
                        overpayAction,
                    }),
                (result) => result.id,
            )

            posthog.capture('pos_checkout_completed', {
                order_id: order.id,
                mode,
                payment_method: paymentMethod,
                total_amount: totalAmount,
                item_count: tab.items.length,
            })

            if (mode === 'save') {
                onOpenChange(false)
                onSuccess()
            } else {
                onOrderCreated?.(order.id, paymentMethod)
                // Show invoice popup for pay_now only
                setInvoiceOrder({
                    id: order.id,
                    code: order.order_code,
                    method: paymentMethod,
                    // M-167: tích hóa đơn + Bán ngay → popup tự phát hành.
                    issueEInvoice: order.issue_einvoice === true,
                })
            }
        } catch {
            // error handled by mutation hook
        }
    }

    return (
        <>
            <Dialog open={open && !invoiceOrder} onOpenChange={onOpenChange}>
                <DialogContent
                    onFocusCapture={() => workflow.interact('details')}
                    className="max-h-[90vh] max-w-md overflow-y-auto"
                >
                    <DialogHeader>
                        <DialogTitle>{m.pos_checkout()}</DialogTitle>
                        <DialogDescription>
                            {tab.customer?.name ?? m.customer_walk_in()}
                        </DialogDescription>
                    </DialogHeader>

                    {/* Customer phone — required, prefilled from customer record */}
                    <div className="space-y-1.5">
                        <Label htmlFor="pos-checkout-phone">
                            {m.customer_phone_label()} *
                        </Label>
                        <Input
                            id="pos-checkout-phone"
                            type="tel"
                            inputMode="tel"
                            autoComplete="tel"
                            value={customerPhone}
                            onChange={(e) => setCustomerPhone(e.target.value)}
                            minLength={8}
                            required
                        />
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor="pos-checkout-shipping-carrier">
                            {m.order_form_carrier_label()}
                        </Label>
                        <ShippingCarrierSelect
                            id="pos-checkout-shipping-carrier"
                            value={selectedCarrierId}
                            onValueChange={(value) => {
                                workflow.step('selection_changed')
                                setSelectedCarrierId(value)
                            }}
                        />
                    </div>

                    {einvoiceEnabled && (
                        <InvoiceBuyerFields
                            idPrefix="pos-checkout"
                            counterSale
                            value={invoiceBuyer}
                            onChange={(patch) =>
                                setInvoiceBuyer((prev) =>
                                    applyBuyerPatch(prev, patch, tab.customer),
                                )
                            }
                        />
                    )}

                    <div className="space-y-1.5">
                        <Label htmlFor="pos-checkout-internal-code">
                            {m.order_internal_code_label()}
                        </Label>
                        <Input
                            id="pos-checkout-internal-code"
                            value={internalCode}
                            maxLength={64}
                            onChange={(e) => setInternalCode(e.target.value)}
                            placeholder={m.order_internal_code_placeholder()}
                        />
                    </div>

                    {customerCredit > 0 && (
                        <label className="flex items-center gap-2 rounded-lg border border-green-300 bg-green-50 p-2 text-sm">
                            <input
                                type="checkbox"
                                checked={applyCredit}
                                onChange={(e) =>
                                    setApplyCredit(e.target.checked)
                                }
                                className="size-4 accent-primary"
                            />
                            <span className="font-medium text-green-800">
                                {m.order_credit_apply_label()} (
                                {formatVND(customerCredit)})
                            </span>
                        </label>
                    )}

                    <PosLotSection lots={lots} />

                    {/* Order total */}
                    <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">
                                {m.order_items_subtotal()}
                            </span>
                            <span>{formatVND(itemSubtotal)}</span>
                        </div>
                        {canEditDiscount && (
                            <div className="flex items-center justify-between gap-2">
                                <label className="text-muted-foreground shrink-0">
                                    {m.order_discount_percent()}
                                </label>
                                <div className="flex items-center gap-1">
                                    <input
                                        type="number"
                                        min={0}
                                        max={100}
                                        step={0.5}
                                        value={orderDiscountPercent}
                                        onChange={(e) =>
                                            setOrderDiscountPercent(
                                                Math.min(
                                                    100,
                                                    Math.max(
                                                        0,
                                                        Number(e.target.value),
                                                    ),
                                                ),
                                            )
                                        }
                                        className="w-14 text-center rounded border border-input bg-background px-2 py-1 text-sm"
                                    />
                                    <span className="text-xs text-muted-foreground">
                                        %
                                    </span>
                                </div>
                            </div>
                        )}
                        {orderDiscountPercent > 0 && (
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">
                                    {m.order_discount_label({
                                        percent: String(orderDiscountPercent),
                                    })}
                                </span>
                                <span className="text-destructive">
                                    -{formatVND(baseAmount - subtotal)}
                                </span>
                            </div>
                        )}
                        {materialTotal > 0 && (
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">
                                    {m.order_material_total()}
                                </span>
                                <span>{formatVND(materialTotal)}</span>
                            </div>
                        )}
                        {vatPercent > 0 && (
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">
                                    {m.order_vat_label({
                                        percent: String(vatPercent),
                                    })}
                                </span>
                                <span>{formatVND(vatAmount)}</span>
                            </div>
                        )}
                    </div>
                    {creditApplied > 0 && (
                        <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">
                                {m.order_credit_apply_label()}
                            </span>
                            <span className="text-green-700">
                                -{formatVND(creditApplied)}
                            </span>
                        </div>
                    )}
                    <div className="flex justify-between text-sm font-semibold">
                        <span>{m.customer_need_to_pay()}</span>
                        <span className="text-primary text-base">
                            {formatVND(amountDue)}
                        </span>
                    </div>

                    <div className="border-t" />

                    <PosCheckoutPayment
                        amountDue={amountDue}
                        customerPayment={customerPayment}
                        onCustomerPaymentChange={setCustomerPayment}
                        overpayAction={overpayAction}
                        onOverpayActionChange={setOverpayAction}
                        hasCustomer={hasCustomer}
                        paymentMethod={paymentMethod}
                        onPaymentMethodChange={setPaymentMethod}
                    />

                    {/* Action buttons */}
                    <div className="flex gap-2 mt-2">
                        {/* Lưu đơn — create order status=new, staff finishes later */}
                        <button
                            type="button"
                            onClick={() => handleSubmit('save')}
                            disabled={!canSubmit}
                            className="flex-1 flex flex-col items-center justify-center gap-1 rounded-lg border border-slate-300 bg-slate-50 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed text-slate-800 font-semibold py-4 text-base transition-colors"
                        >
                            <Save className="size-5" />
                            {m.pos_save_order()}
                        </button>
                        {/* Bán ngay — full cashier flow */}
                        <button
                            type="button"
                            onClick={() => handleSubmit('pay_now')}
                            disabled={!canSubmit}
                            className="flex-1 flex flex-col items-center justify-center gap-1 rounded-lg bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-primary-foreground font-bold py-4 text-base transition-colors"
                        >
                            <Zap className="size-5" />
                            {checkout.isPending
                                ? m.common_processing()
                                : m.pos_buy_now()}
                            <span className="text-xs font-semibold opacity-90">
                                {formatVND(amountDue)}
                            </span>
                        </button>
                    </div>
                </DialogContent>
            </Dialog>

            {invoiceOrder && (
                <PosInvoicePopup
                    open={true}
                    orderId={invoiceOrder.id}
                    orderCode={invoiceOrder.code}
                    paymentMethod={invoiceOrder.method}
                    issueEInvoice={invoiceOrder.issueEInvoice}
                    onDone={() => {
                        setInvoiceOrder(null)
                        onOpenChange(false)
                        onSuccess()
                    }}
                />
            )}
        </>
    )
}
